/**
 * CustomerDetailPanel — Etapa 5 + refino Etapa 7 (P1-3).
 *
 * Resumo do cliente (somente leitura). Variant "panel" (desktop) ou
 * "sheet" (mobile). Reusa classes .ipwd-rp-* e o helper puro
 * incidentRules.js para a seção "Incidentes deste cliente" e o motivo
 * principal de cada device. Botões só de navegação/contexto (sem escrita).
 */
import { DOMINANT_COLOR, DOMINANT_LABEL } from "./customerGroups.js";
import { buildIncidents, deviceIncident } from "./incidentRules.js";

function Stat({ label, value, color }) {
  return (
    <div className="ipwd-rp-row">
      <span className="ipwd-rp-row-label">{label}</span>
      <span className="ipwd-rp-row-val" style={color ? { color } : undefined}>
        {value}
      </span>
    </div>
  );
}

function statusDot(d) {
  return !d.online ? "#FF5063"
    : d.relay === "off" ? "#FF8A3C"
    : d.weak_signal ? "#FFB224"
    : "#22c55e";
}

function Body({
  group, onFilterCustomer, onOpenOperacional, onFocusDevice, onFocusOnMap,
}) {
  const color = DOMINANT_COLOR[group.dominant] || DOMINANT_COLOR.nodata;
  const now = Date.now();

  const devs = [...group.devices].sort((a, b) => {
    const rank = (d) => (!d.online ? 0 : d.relay === "off" ? 1 : d.weak_signal ? 2 : 3);
    return rank(a) - rank(b) || a.device_id.localeCompare(b.device_id);
  });

  const incidents = buildIncidents(group.devices, now).slice(0, 5);
  const canCustomerNav = !group.isNone && !!group.id;
  const canFocus = group.lat != null && group.lng != null;

  return (
    <>
      <div className="ipwd-rp-status">
        <span style={{ color }}>● {DOMINANT_LABEL[group.dominant] || "—"}</span>
        <span className="ipwd-rp-relay">{group.total} dispositivos</span>
      </div>

      {/* Resumo */}
      <div className="ipwd-rp-section">
        <Stat label="Código" value={group.code || "—"} />
        <Stat label="Online" value={group.online} color="#35E8A4" />
        <Stat label="Offline" value={group.offline} color={group.offline ? "#f87171" : undefined} />
        <Stat label="Relé OFF" value={group.relayOff} color={group.relayOff ? "#fb923c" : undefined} />
        <Stat label="RSSI fraco" value={group.weak} color={group.weak ? "#FFB224" : undefined} />
        <Stat label="Score médio" value={group.scoreAvg != null ? group.scoreAvg : "—"} />
      </div>

      {/* Incidentes deste cliente */}
      <div className="ipwd-rp-section">
        <div className="ipwd-rp-section-title">Incidentes deste cliente</div>
        {incidents.length === 0 ? (
          <div className="ipwd-rp-na">Nenhum incidente ativo</div>
        ) : (
          <div className="ipwd-incidents">
            {incidents.map((it) => (
              <button
                key={it.device_id}
                type="button"
                className="ipwd-incident"
                onClick={() => onFocusDevice?.(it.device_id)}
                title={`Focar ${it.device_id}`}
              >
                <span className="ipwd-incident-dot" style={{ background: it.color }} />
                <span className="ipwd-incident-main">
                  <span className="ipwd-incident-id">{it.device_id}</span>
                  <span className="ipwd-incident-sub" style={{ color: it.color }}>
                    {it.reason}
                  </span>
                </span>
                {it.score != null && (
                  <span className="ipwd-incident-score">{it.score}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lista de devices */}
      <div className="ipwd-rp-section">
        <div className="ipwd-rp-section-title">Dispositivos ({devs.length})</div>
        <div className="ipwd-cust-devlist">
          {devs.map((d) => {
            const inc = deviceIncident(d, now);
            const meta = d.rssi != null
              ? `${d.rssi} dBm`
              : d.health_score != null
                ? `score ${d.health_score}`
                : "—";
            return (
              <button
                key={d.device_id}
                type="button"
                className="ipwd-cust-devitem"
                onClick={() => onFocusDevice?.(d.device_id)}
                title={`Focar ${d.device_id} no modo operacional`}
              >
                <span className="ipwd-cust-devdot" style={{ background: statusDot(d) }} />
                <span className="ipwd-cust-devmain">
                  <span className="ipwd-cust-devid">{d.device_id}</span>
                  {inc && (
                    <span className="ipwd-cust-devreason" style={{ color: inc.color }}>
                      {inc.reason}
                    </span>
                  )}
                </span>
                <span className="ipwd-cust-devmeta">
                  <span className="ipwd-cust-devrelay">
                    {d.relay ? d.relay.toUpperCase() : "—"}
                  </span>
                  <span className="ipwd-cust-devrssi">{meta}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Navegação (sem escrita) */}
      <div className="ipwd-rp-actions ipwd-rp-actions-col">
        <button
          type="button"
          className="ipwd-rp-navbtn is-primary"
          disabled={!canCustomerNav}
          onClick={() => onOpenOperacional?.(group)}
        >
          Ver devices no operacional
        </button>
        <div className="ipwd-rp-actions">
          <button
            type="button"
            className="ipwd-rp-navbtn"
            disabled={!canCustomerNav}
            onClick={() => onFilterCustomer?.(group)}
          >
            Filtrar este cliente
          </button>
          <button
            type="button"
            className="ipwd-rp-navbtn"
            disabled={!canFocus}
            onClick={() => onFocusOnMap?.(group)}
          >
            Focar no mapa
          </button>
        </div>
      </div>
    </>
  );
}

export default function CustomerDetailPanel({
  variant = "panel",
  group,
  onClose,
  onFilterCustomer,
  onOpenOperacional,
  onFocusDevice,
  onFocusOnMap,
}) {
  if (!group) return null;

  const head = (
    <div className="ipwd-rp-head">
      <span className="ipwd-rp-title">🏢 {group.label}</span>
      <button type="button" className="ipwd-rp-close" title="Fechar" onClick={onClose}>×</button>
    </div>
  );

  const body = (
    <Body
      group={group}
      onFilterCustomer={onFilterCustomer}
      onOpenOperacional={onOpenOperacional}
      onFocusDevice={onFocusDevice}
      onFocusOnMap={onFocusOnMap}
    />
  );

  if (variant === "sheet") {
    return (
      <>
        <div className="ipwd-mobile-sheet-backdrop" onClick={onClose} />
        <div className="ipwd-mobile-sheet" role="dialog" aria-label="Resumo do cliente">
          <div className="ipwd-sheet-handle" />
          {head}
          {body}
        </div>
      </>
    );
  }

  return (
    <aside className="ipwd-fleet-rightpanel hidden md:flex">
      {head}
      {body}
    </aside>
  );
}
